/* Enter → the surface's STREAM (W6 REV 3).
 *
 *   RECORD KEEPING       → the W4 deck pager, gates first. The pager IS
 *                          the stream (no queue component); the walk is
 *                          a VIEW alongside it, never a queue.
 *   OV / CHASE / OPS     → the ONE reusable queue component, instantiated
 *                          per surface — never across surfaces.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import type { TaskQueueItem } from '../../lib/tasks'
import { RkDecksSurface } from '../rkdecks/RkDecksSurface'
import { adminTasks, fetchOpenTasks, tasksForSurface } from './api'
import { SURFACE_NAMES, surfaceForSlug } from './placement'
import { fetchFundLedgers } from '../ledger'
import { openWords, queueNoun, queueOpen, type FundLedgerList } from '../ledgerWords'
import FundSelector from '../FundSelector'
import { useFundScope } from '../fundScope'
import ChaseSurface from './ChaseSurface'
import SurfaceQueue from './SurfaceQueue'
import SystemFailures from './SystemFailures'
import WalkView from './WalkView'
import { ErrorState, Skeleton } from '../state'
import { HelpDot } from '../guidance/Guidance'
import './workbench.css'
import { useContextualNavigation } from '../navigation/useRead'
import ContextWorkbenchRoute from './ContextWorkbenchRoute'

export default function SurfaceStream() {
  const contextual = useContextualNavigation()
  const { surface } = useParams()
  if (surface === 'other-ops') {
    if (contextual.loading) return <Skeleton kind="cards" n={1} height={260} />
    if (contextual.error || typeof contextual.value?.enabled !== 'boolean')
      return (
        <ErrorState
          what="Workbench context"
          error={contextual.error ?? new Error('Context availability could not be read.')}
        />
      )
  }
  if (contextual.value?.enabled) return <ContextWorkbenchRoute />
  return <LegacySurfaceStream />
}

function LegacySurfaceStream() {
  const { surface: slug } = useParams()
  const surface = surfaceForSlug(slug)
  const [params] = useSearchParams()
  /* The fund scope is the ONE scope (Tier-2 selector ruling): the queue's
   * title carries the same control Today and the Workbench carry, and the
   * choice arrives here in `?fund=` — the pager, the queue rows and the
   * walk all read it. The record-keeping pager's own "switch fund" menu
   * is retired: one control, not three. */
  const { fundId, setFund } = useFundScope()
  const taskId = params.get('task')
  const navigate = useNavigate()
  const [tasks, setTasks] = useState<TaskQueueItem[] | null>(null)
  const [ledgers, setLedgers] = useState<FundLedgerList | null>(null)
  const [error, setError] = useState<unknown>(null)
  // Retry (state system): a fresh read and a fresh heartbeat clock.
  const [tick, setTick] = useState(0)

  /* `background === true` only from the heartbeat below — the idle clock
   * (lib/api.ts) must not move on a timer. */
  const load = useCallback(
    (background = false) => {
      fetchOpenTasks(fundId, background === true)
        .then((items) => {
          setTasks(items)
          setError(null)
        })
        .catch((err: unknown) => setError(err))
    },
    [fundId]
  )

  /* Heartbeat (Massimo 2026-08-30): streams refresh themselves. */
  useEffect(() => {
    load()
    const t = setInterval(() => load(true), 15000)
    return () => clearInterval(t)
  }, [load, tick])
  const retry = () => {
    setError(null)
    setTasks(null)
    setTick((t) => t + 1)
  }

  /* The selector's counts come from the bulk ledger — the one read every
   * screen counts from — never re-derived here. On a queue each fund
   * carries THAT queue's own figure. */
  useEffect(() => {
    let cancelled = false
    const pull = (background = false) => {
      fetchFundLedgers(background)
        .then((l) => {
          if (!cancelled) setLedgers(l)
        })
        .catch(() => {
          if (!cancelled) setLedgers(null)
        })
    }
    pull()
    const t = setInterval(() => pull(true), 15000)
    return () => {
      cancelled = true
      clearInterval(t)
    }
  }, [])

  const selectorFunds = useMemo(
    () =>
      (ledgers?.funds ?? []).map((f) => ({
        id: f.fund_id,
        name: f.fund_name,
        open: queueOpen(surface, f),
        sub: openWords(f),
      })),
    [ledgers, surface]
  )

  const selector = (
    <FundSelector
      funds={selectorFunds}
      value={fundId}
      onChoose={setFund}
      noun={queueNoun(surface)}
      loading={!ledgers}
    />
  )

  if (!surface)
    return (
      <main className="wb">
        <div className="inner">
          <p className="sub">No such queue.</p>
        </div>
      </main>
    )

  /* Back keeps the fund chip held on the landing (the officer scoped
   * the workbench to a fund; leaving a stream must not silently widen it). */
  const back = (
    <button
      type="button"
      className="backlink"
      onClick={() =>
        navigate(`/v2/workbench${fundId ? `?fund=${encodeURIComponent(fundId)}` : ''}`)
      }
    >
      ← Workbench
    </button>
  )

  if (surface === 'record_keeping')
    return (
      <main className="wb" data-testid="wb-stream-record_keeping">
        <div className="inner">
          <div className="streamhead">
            {back}
            <h1>
              {SURFACE_NAMES.record_keeping} — {selector}
            </h1>
          </div>
          {/* The deck LIST is this queue's stream — gates first, and all
              four stages in it: decide · book · reconcile · walk.
              Look-through and chain-head election are deck stages here,
              never their own line and never a separate queue, so every
              row the RECORD KEEPING line counts is a line of this list.
              A ?task= deep link seeds the selection on that task's own
              deck (walk and reconcile decks are `task:<id>`), so the
              link lands on the card instead of an unfiltered stream. The
              ?fund= scope is read by the list itself from the URL — the
              ONE selector in the title above writes it — so the deep
              link, the selector and the walk view below all see one
              fund. The title is this header's: the list carries no
              second one. */}
          <RkDecksSurface initialDeckId={taskId ? `task:${taskId}` : null} heading={false} />
          <WalkView tasks={tasks ?? []} />
        </div>
      </main>
    )

  /* Chase surface (locked 2026-08-31): the letter cards — one card per
   * counterparty per letter, request/chase split, sent-tracking OUT. */
  if (surface === 'chase_request')
    return (
      <main className="wb" data-testid="wb-stream-chase_request">
        <div className="inner">
          <div className="streamhead">
            {back}
            <h1>
              {SURFACE_NAMES.chase_request} — {selector}
              {/* The type-symbol key sits behind the ? (audit cut 4). */}
              <HelpDot k="queue.chase_request" />
            </h1>
          </div>
          <ChaseSurface />
        </div>
      </main>
    )

  return (
    <main className="wb" data-testid={`wb-stream-${surface}`}>
      <div className="inner">
        <div className="streamhead">
          {back}
          <h1>
            {SURFACE_NAMES[surface]} — {selector}
          </h1>
        </div>
        {error ? <ErrorState what="queue" error={error} onRetry={retry} /> : null}
        {tasks === null ? (
          error ? null : (
            <Skeleton kind="rows" n={6} height={41} />
          )
        ) : (
          <SurfaceQueue
            surface={surface}
            tasks={tasksForSurface(tasks, surface)}
            onChanged={load}
          />
        )}
        {/* The visibility line's interior: machine failures, never
            officer queue rows, with the one verb the officer keeps. */}
        {surface === 'other_ops' && tasks !== null ? (
          <SystemFailures tasks={adminTasks(tasks)} onChanged={load} />
        ) : null}
      </div>
    </main>
  )
}
